import { ShotZones } from "../app/ShotZones";
import { TagBadge } from "../app/kit";
import { Reveal } from "./motion";

const Card = ({ className = "", children, testid }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <div onMouseMove={onMove} data-testid={testid} className={`spot-card group relative h-full overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0F172A] p-6 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-[#39FF14]/40 sm:p-8 ${className}`}>
      {children}
    </div>
  );
};

const Title = ({ k, t, d }) => (
  <div className="relative">
    <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">{k}</p>
    <h3 className="mt-2 font-display text-3xl font-extrabold uppercase leading-none text-white sm:text-4xl">{t}</h3>
    {d && <p className="mt-3 max-w-md text-sm text-slate-400 sm:text-base">{d}</p>}
  </div>
);

const SAMPLE_ZONES = { rim: 64, paint: 52, midrange: 38, corner3: 41, wing3: 33, top3: 36 };
const BENCH = [["Running vert", 36, "in"], ["Wingspan", 78, "in"], ["Lane agility", 10.9, "s"]];
const TREND = [8, -4, 12, 6, -2, 15, 9, 3, 18, -6, 11, 14];

export const Bento = () => (
  <section id="features" className="relative mx-auto max-w-[1400px] px-5 py-28 sm:px-10 sm:py-36">
    <Reveal className="mb-16 grid gap-6 lg:grid-cols-12">
      <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-7xl lg:col-span-7">Everything the<br />scorebook <span className="text-[#39FF14]">forgot.</span></h2>
      <p className="self-end text-base text-slate-400 md:text-lg lg:col-span-5">Every number your staff logs in the GAMEDAY app, laid out on a screen big enough to scout with.</p>
    </Reveal>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:auto-rows-[minmax(260px,auto)]">
      <Reveal className="md:col-span-7 md:row-span-2">
        <Card testid="bento-shot-zones">
          <Title k="01 / Shooting" t="Shot zones, not guesswork." d="Rim to top of the key, every make and miss mapped by zone, alongside eFG% and TS%." />
          <div className="relative mt-8"><ShotZones zones={SAMPLE_ZONES} testid="bento-court" /></div>
          <p className="relative mt-4 font-mono-gd text-[11px] uppercase tracking-widest text-slate-500">Sample data</p>
        </Card>
      </Reveal>
      <Reveal className="md:col-span-5" delay={0.08}>
        <Card testid="bento-board">
          <Title k="02 / Recruiting" t="A board that sorts itself." />
          <div className="relative mt-6 space-y-3">
            {[["Top Target", 82], ["Monitoring", 56], ["Backup", 30]].map(([t, w]) => (
              <div key={t} className="flex items-center gap-4">
                <div className="w-28 shrink-0"><TagBadge tag={t} /></div>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#162032]">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#39FF14] to-[#39FF14]/40 transition-[width] duration-700 group-hover:w-full" style={{ width: `${w}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </Reveal>
      <Reveal className="md:col-span-5" delay={0.14}>
        <Card testid="bento-combine">
          <Title k="03 / Combine" t="Measured against the draft." />
          <div className="relative mt-6 grid grid-cols-3 gap-3">
            {BENCH.map(([l, v, u]) => (
              <div key={l} className="rounded-2xl bg-[#162032] p-3">
                <p className="font-mono-gd text-[10px] uppercase tracking-wider text-slate-400">{l}</p>
                <p className="font-mono-gd text-2xl font-bold text-white">{v}<span className="text-sm text-slate-400">{u}</span></p>
              </div>
            ))}
          </div>
          <p className="relative mt-3 text-xs text-slate-400">Benchmark: average of point guards later drafted into the NBA, measured at the 2000–2018 NBA Draft Combine. A pro-prospect target, not a high school average.</p>
        </Card>
      </Reveal>
      <Reveal className="md:col-span-4" delay={0.05}>
        <Card testid="bento-trajectory">
          <Title k="04 / Program" t="Trajectory." />
          <div className="relative mt-8 flex h-32 gap-1.5 border-[#1E293B]">
            <span className="absolute inset-x-0 top-1/2 h-px bg-slate-700" />
            {TREND.map((m, i) => (
              <div key={i} className="relative h-full flex-1">
                <div className={`absolute inset-x-0 rounded-sm transition-transform duration-500 group-hover:scale-y-110 ${m >= 0 ? "bottom-1/2 origin-bottom bg-[#39FF14]" : "top-1/2 origin-top bg-[#FF3B30]"}`} style={{ height: `${Math.abs(m) * 2.6}%` }} />
              </div>
            ))}
          </div>
          <p className="relative font-mono-gd text-[11px] uppercase tracking-widest text-slate-500">Point margin · sample</p>
        </Card>
      </Reveal>
      <Reveal className="md:col-span-4" delay={0.1}>
        <Card testid="bento-depth">
          <Title k="05 / Lineup" t="Depth chart." />
          <div className="relative mt-8 grid grid-cols-5 gap-2">
            {["PG", "SG", "SF", "PF", "C"].map((p, i) => (
              <div key={p} className="rounded-xl border border-white/5 bg-[#162032] py-4 text-center transition-colors duration-300 group-hover:border-[#39FF14]/40" style={{ transitionDelay: `${i * 60}ms` }}>
                <p className="font-display text-2xl font-black text-[#39FF14]">{p}</p>
              </div>
            ))}
          </div>
        </Card>
      </Reveal>
      <Reveal className="md:col-span-4" delay={0.15}>
        <Card testid="bento-messages">
          <Title k="06 / Messaging" t="Straight to the coach." />
          <div className="relative mt-6 space-y-2 text-sm">
            <p className="w-fit max-w-[85%] rounded-2xl rounded-bl-sm bg-[#162032] px-4 py-2 text-slate-100">Is your PG open to an official visit?</p>
            <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-[#39FF14] px-4 py-2 font-medium text-[#0A0E1A]">Yes. Sending film now.</p>
          </div>
        </Card>
      </Reveal>
    </div>
  </section>
);
